/**
 * Log-safe summaries of user content.
 *
 * Logs (Vercel, Trigger, Convex, Axiom) sit outside the user's silo, so they
 * must never carry email subjects/bodies, contact names, document text or file
 * names, calendar titles, prompts, model output, captions or raw provider
 * payloads. Log a shape instead: ids, counts, lengths, extensions, error types.
 *
 * Deliberately no content hashes: most user strings are low-entropy (names,
 * titles) and a hash of them is brute-forceable.
 */

const SAFE_LABEL = /^[A-Za-z0-9_.:-]{1,64}$/;

/** Length of a string (or array), 0 for anything else. */
export function logTextLength(value: unknown): number {
  if (typeof value === "string" || Array.isArray(value)) return value.length;
  return 0;
}

/** Lowercase file extension without the name, or "none". */
export function logFileExtension(name: unknown): string {
  if (typeof name !== "string") return "none";
  const base = name.split(/[\\/]/).pop() ?? "";
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return "none";
  const extension = base.slice(dot + 1).toLowerCase();
  return /^[a-z0-9]{1,10}$/.test(extension) ? extension : "other";
}

/** A value only if it is already a short machine label; otherwise "redacted". */
export function logSafeLabel(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return typeof value === "string" && SAFE_LABEL.test(value) ? value : "redacted";
}

export type LogErrorEvidence = {
  errorType: string;
  errorStatus?: number;
  errorCode?: string;
};

/**
 * Error type, HTTP status and code, without the message: provider and model
 * errors often echo the request (subjects, prompts, addresses) back.
 */
export function logErrorEvidence(error: unknown): LogErrorEvidence {
  if (!error || typeof error !== "object") return { errorType: typeof error };
  const record = error as Record<string, unknown>;
  // Subclasses that never set `name` still report "Error"; the constructor is
  // the more useful label then.
  const constructorName = typeof record.constructor === "function" ? record.constructor.name : undefined;
  const name = error instanceof Error && error.name !== "Error"
    ? error.name
    : constructorName || (error instanceof Error ? error.name : undefined);
  const evidence: LogErrorEvidence = { errorType: logSafeLabel(name ?? "object") };
  const status = record.status ?? record.statusCode;
  if (typeof status === "number" && Number.isFinite(status)) evidence.errorStatus = status;
  if (record.code !== undefined) evidence.errorCode = logSafeLabel(record.code);
  return evidence;
}

export type LogValueShape =
  | "null"
  | "undefined"
  | "boolean"
  | "number"
  | { string: number }
  | { array: number }
  | { object: string[] }
  | string;

/**
 * Structural shape of a payload: strings become lengths, arrays counts, and
 * objects their (label-safe) top-level keys. Never includes values.
 */
export function logValueShape(value: unknown): LogValueShape {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (typeof value === "string") return { string: value.length };
  if (Array.isArray(value)) return { array: value.length };
  if (typeof value === "object") {
    return { object: Object.keys(value).slice(0, 25).map(logSafeLabel) };
  }
  return typeof value;
}
